import { Layout, message } from "antd";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import CloseIcon from "../../../assets/icons/close-with-shadow.svg";
import GiftIcon from "../../../assets/icons/gift.svg";
import ShareIcon from "../../../assets/icons/share.svg";
import UserIcon from "../../../assets/icons/user-white.svg";
import VerifyIcon from "../../../assets/icons/verify.svg";
import AvatarComponent from "../../../components/AvatarComponent";
import ChallengeProgressionBarComponent from "../../../components/ChallengeLive/ChallengeProgressionBarComponent";
import FanLiveBroadcastSubscriber from "../../../components/FanLiveBroadcastSubscriber/FanLiveBroadcastSubscriber";
import LiveTipModalComponent from "../../../components/LiveTipModalComponent";
import Loader from "../../../components/loader/Loader";
import ShareModalComponent from "../../../components/ShareModalComponent";
import AgoraProvider from "../../../helpers/agoraProvider";
import { EURO_SYMBOLE, PAYMENT_TYPES, USER_TYPES } from "../../../helpers/constant";
import { ILiveTip } from "../../../helpers/types";
import { addLiveComment, clearStreamStats, newLiveTip, updateStreamStats } from "../../../redux/lives/liveDataSlice";
import { clearToken, editLive, getLiveById, updateEndLive } from "../../../redux/lives/livesSlice";
import { AppDispatch, StoreState } from "../../../redux/store";
import { liveService } from "../../../services";
import { getSocket, offSocketReconnected, onSocketReconnected } from "../../../socket";
import { getUserColor } from "../../../utls/colors";
import { formatNumber } from "../../../utls/FunctionsUtil";
import "./index.less";

const FanLiveBroadcastPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const subscriberRef = useRef<any>(null);
  const joinedRef = useRef<boolean>(false);
  const creatorRef = useRef<any>(null);
  const dispatch = useDispatch<AppDispatch>();
  const live = useSelector((state: StoreState) => state.lives.item);
  const liveComments = useSelector((state: StoreState) => state.liveStreamStats.comments.list);
  const streamStats = useSelector((state: StoreState) => state.liveStreamStats.streamStats.stats);
  const liveTip = useSelector((state: StoreState) => state.liveStreamStats.tips.tips);
  const [token, setToken] = useState("");
  const isLoading = useSelector((state: StoreState) => state.lives.loading);
  const [isLandscape, setIsLandscape] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [commentText, setCommentText] = useState("");
  const commentsEndRef = useRef<HTMLDivElement>(null);
  const controlsTimeoutRef = useRef<any>(null);
  const [isTipModalOpen, setIsTipModalOpen] = useState<boolean>(false);
  const [isPersonalAmount, setIsPersonalAmount] = useState<boolean>(false);
  const [tipAmount, setTipAmount] = useState<number | null>(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState<boolean>(false);

  useEffect(() => {
    if (!id) return;
    dispatch(getLiveById(id));
  }, [id, dispatch]);

  useEffect(() => {
    if (live?.creator) {
      creatorRef.current = live.creator;
    }
  }, [live?.creator]);

  useEffect(() => {
    if (!live || !live.sessionId || joinedRef.current) return;
    const getToken = async (sessionId: string) => {
      await liveService.buildToken(sessionId, false).then((responseToken) => {
        if (responseToken && typeof responseToken === "string") {
          setToken(responseToken);
        }
      });
    };
    getToken(live.sessionId);
  }, [live, id, dispatch]);

  useEffect(() => {
    if (token) {
      const joinLive = async () => {
        if (!token) return;
        subscriberRef.current && (await subscriberRef.current.join(token));
        const socket = getSocket();
        socket.emit("public-stream/join", { id });
      };
      joinLive();
    }
  }, [token]);

  const leaveLive = async () => {
    subscriberRef.current && (await subscriberRef.current.leave());
    const socket = getSocket();
    socket.emit("public-stream/leave", { id, role: USER_TYPES.FAN });
  };

  const onStreamStatusChanged = (value: boolean) => {
    joinedRef.current = value;
  };

  const unSubscribed = async (streamingTime: number) => {
    const socket = getSocket();
    socket.emit("public-stream/leave", { id, streamingTime, role: USER_TYPES.CREATOR });
  };

  const userJoined = async (data: any) => {
    if (data.streamStats) await dispatch(updateStreamStats(data.streamStats));
    if (data.comments) await dispatch(addLiveComment(data.comments));
    if (data.lastStreamTip) await dispatch(newLiveTip(data.lastStreamTip));
  };

  const addedComment = async (data: any) => {
    await dispatch(addLiveComment(data.comments));
  };

  const tipUpdated = async (data: any) => {
    if (data?.tipData) {
      await dispatch(newLiveTip(data.tipData));
      showControlsTemporarily();
    }
    if (data?.totalPrice) {
      await dispatch(
        editLive({
          ...live,
          creator: creatorRef.current,
          tipPrice: data.totalPrice,
          currentChallenge: data.currentChallenge || live?.currentChallenge,
        })
      );
    }
  };

  const userLeaved = async (data: any) => {
    if (data?.streamStats && !data?.isStreamingEnds) {
      await dispatch(updateStreamStats(data?.streamStats));
    }
    if (data?.isStreamingEnds) {
      dispatch(updateEndLive({ streamId: data?.streamId, isStreamingEnds: data?.isStreamingEnds }));
      await dispatch(clearStreamStats());
      navigate("/p/fan/lives");
    }
  };

  const handleChallengeCreatedOrDeleted = (data: any) => {
    if (data?.currentChallenge) {
      dispatch(editLive({ ...live, creator: creatorRef.current, currentChallenge: data.currentChallenge }));
      showControlsTemporarily();
    }
    if (data?.isDeleted) {
      dispatch(editLive({ ...live, creator: creatorRef.current, currentChallenge: null }));
    }
  };

  useEffect(() => {
    const socket = getSocket();
    socket.on("stream_challenge", handleChallengeCreatedOrDeleted);
    return () => {
      socket.off("stream_challenge", handleChallengeCreatedOrDeleted);
    };
  }, [dispatch, live]);

  useEffect(() => {
    const registerLiveEventListeners = () => {
      const socket = getSocket();
      socket.off("user_joined", userJoined);
      socket.off("comment_added", addedComment);
      socket.off("tip_updated", tipUpdated);
      socket.off("user_leaved", userLeaved);

      socket.on("user_joined", userJoined);
      socket.on("comment_added", addedComment);
      socket.on("tip_updated", tipUpdated);
      socket.on("user_leaved", userLeaved);
    };

    const unregisterLiveEventListeners = () => {
      const socket = getSocket();
      socket.off("user_joined", userJoined);
      socket.off("comment_added", addedComment);
      socket.off("tip_updated", tipUpdated);
      socket.off("user_leaved", userLeaved);
    };

    const handleSocketReconnected = (event: CustomEvent) => {
      registerLiveEventListeners();

      if (joinedRef.current && id) {
        const socket = getSocket();
        socket.emit("public-stream/join", { id });
      }
    };

    registerLiveEventListeners();
    onSocketReconnected(handleSocketReconnected);

    return () => {
      unregisterLiveEventListeners();
      offSocketReconnected(handleSocketReconnected);

      if (joinedRef.current) {
        leaveLive();
      }
    };
  }, [id, dispatch]);

  useEffect(() => {
    if (live && !live?.isStreaming && live?.isEndStreaming) {
      navigate("/fan/home");
    }
  }, [live]);

  useEffect(() => {
    const handleOrientationChange = () => {
      const landscape = window.matchMedia("(orientation: landscape)").matches;
      setIsLandscape(landscape);
    };

    handleOrientationChange();
    window.addEventListener("resize", handleOrientationChange);
    window.addEventListener("orientationchange", handleOrientationChange);

    return () => {
      window.removeEventListener("resize", handleOrientationChange);
      window.removeEventListener("orientationchange", handleOrientationChange);
    };
  }, []);

  useEffect(() => {
    if (commentsEndRef.current) {
      commentsEndRef.current.scrollIntoView({ behavior: "auto", block: "end" });
    }
  }, [liveComments]);

  const handleClose = async () => {
    if (document.fullscreenElement && document.exitFullscreen) {
      await document.exitFullscreen();
    }
    navigate("/p/fan/lives?tab=now");
  };

  const handleControlsVisibilityChange = (visible: boolean) => {
    setShowControls(visible);
  };

  const showControlsTemporarily = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 5000);
  };

  const onShareClick = () => {
    setIsShareModalOpen(true);
  };

  const closeShareModal = () => {
    setIsShareModalOpen(false);
  };

  const truncateUsername = (username: string) => {
    if (username.length > 10) {
      return username.substring(0, 10) + "...";
    }
    return username;
  };

  const handleSendComment = () => {
    if (!commentText.trim() || !id) return;

    const socket = getSocket();
    socket.emit("public-stream/add-comment", {
      id,
      text: commentText.trim(),
    });

    setCommentText("");
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSendComment();
    }
  };

  const onSubmitTip = () => {
    if (!id || !tipAmount) return message.warning("Please select amount.");

    liveService
      .createTip(id, tipAmount)
      .then((data: ILiveTip) => {
        if (data?._id) {
          dispatch(clearToken());
          navigate(
            `/fan/payment?creatorId=${live?.creator?._id}&type=${PAYMENT_TYPES.LIVE_TIP}&liveTipId=${data._id}&redirectUrl=fan/live-broadcast/${live?._id}`
          );
        }
      })
      .catch((error) => {});
  };

  return (
    <AgoraProvider config={{ mode: "live", codec: "h264", role: "audience" }}>
      <Layout>
        <Layout.Content className="fan-broadcast-content-wrapper">
          {isLoading ? (
            <div className="fan-broadcast-loading-wrapper">
              <Loader />
            </div>
          ) : (
            live && (
              <div className="fan-broadcast-page-wrapper">
                <div className={`fan-broadcast-main-container ${isLandscape ? "landscape" : ""}`}>
                  <div className="fan-broadcast-live-section">
                    <FanLiveBroadcastSubscriber
                      sessionId={live?.sessionId || null}
                      remoteId={live?.creator?._id || null}
                      ref={subscriberRef}
                      onStreamStatusChange={onStreamStatusChanged}
                      unSubscribed={unSubscribed}
                      isFromMobile={live?.isFromMobile || false}
                      objectFit="contain"
                      showControls={true}
                      onControlsVisibilityChange={handleControlsVisibilityChange}
                    />

                    <div
                      className="fan-broadcast-header"
                      style={{
                        opacity: showControls ? 1 : 0,
                        pointerEvents: showControls ? "auto" : "none",
                      }}
                    >
                      <div className="fan-broadcast-header-left">
                        <AvatarComponent image={live?.creator?.avatar} size={25} />
                        <div className="fan-broadcast-username">
                          {live?.creator?.username} {live?.creator?.verifiedEmail && <img src={VerifyIcon} alt="verify" />}
                        </div>
                      </div>
                      {live?.currentChallenge && !isLandscape && (
                        <div className="fan-broadcast-challenge-progression">
                          <ChallengeProgressionBarComponent challenge={live?.currentChallenge} />
                        </div>
                      )}
                      <div className="fan-broadcast-header-right">
                        {live && streamStats && (
                          <div className="fan-broadcast-spectators-tag">
                            <img src={UserIcon} width={10} alt="user" />
                            <div className="fan-broadcast-spectators-count">{formatNumber(streamStats.members)}</div>
                          </div>
                        )}
                        <img src={ShareIcon} width={22} alt="share" onClick={onShareClick} style={{ cursor: "pointer" }} />
                        <img src={GiftIcon} width={22} alt="gift" onClick={() => setIsTipModalOpen(true)} style={{ cursor: "pointer" }} />
                        <img src={CloseIcon} width={22} alt="close" onClick={handleClose} style={{ cursor: "pointer" }} />
                      </div>
                    </div>
                  </div>

                  <div className="fan-broadcast-chat-section">
                    {live?.currentChallenge && isLandscape ? (
                      <div className="fan-broadcast-challenge-header">
                        <ChallengeProgressionBarComponent challenge={live?.currentChallenge} />
                      </div>
                    ) : (
                      <div className="fan-broadcast-chat-header">
                        <div className="fan-broadcast-chat-title">Chat</div>
                        <div className="fan-broadcast-tips-container">
                          {liveTip && (
                            <div className="fan-broadcast-last-tip">
                              <span className="fan-broadcast-tip-username">{truncateUsername(liveTip.username)}</span>
                              <span className="fan-broadcast-tip-amount">
                                {parseFloat(liveTip.amount).toFixed(2)} {EURO_SYMBOLE}
                              </span>
                            </div>
                          )}
                          {live?.tipPrice !== undefined && live.tipPrice > 0 && (
                            <div className="fan-broadcast-total-tip">
                              <span className="fan-broadcast-tip-total">
                                {live.tipPrice.toFixed(2)} {EURO_SYMBOLE}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="fan-broadcast-comments-list">
                      <div className="fan-broadcast-comments-wrapper">
                        {liveComments
                          ?.slice()
                          .reverse()
                          .map((comment, index) => {
                            const userColor = getUserColor(comment?.user?.username || "");
                            return (
                              <div key={index} className="fan-broadcast-comment-item">
                                <div className="fan-broadcast-username-text" style={{ color: userColor }}>
                                  {comment?.user?.username}
                                </div>
                                <div className="fan-broadcast-comment-text">{comment?.text}</div>
                              </div>
                            );
                          })}
                        <div ref={commentsEndRef} />
                      </div>
                    </div>

                    <div className="fan-broadcast-input-container">
                      <input
                        type="text"
                        className={`fan-broadcast-comment-input ${commentText ? "has-suffix" : ""}`}
                        placeholder={t("write-message")!}
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        onKeyPress={handleKeyPress}
                      />
                      {commentText && (
                        <div className={`fan-broadcast-input-suffix ${isLandscape ? "landscape" : ""}`} onClick={handleSendComment}>
                          <span className="fan-broadcast-send-label">{t("send")}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )
          )}
        </Layout.Content>
      </Layout>
      <LiveTipModalComponent
        userName={live?.creator?.name || null}
        isOpen={isTipModalOpen}
        amount={tipAmount}
        isPersonalAmount={isPersonalAmount}
        setIsPersonalAmount={(value) => setIsPersonalAmount(value)}
        onChangeValue={(value) => setTipAmount(value)}
        onSubmit={onSubmitTip}
        onClose={() => {
          setTipAmount(null);
          setIsTipModalOpen(false);
        }}
      />
      <ShareModalComponent
        isOpen={isShareModalOpen}
        onClose={closeShareModal}
        id={live?.creator?._id}
        username={live?.creator?.username}
        link={`${window.location.origin}/fan/live-broadcast/${live?._id}`}
      />
    </AgoraProvider>
  );
};

export default FanLiveBroadcastPage;
